/**
 * EmptyState Component
 *
 * Displays encouraging empty states for each analytics section.
 * Includes relevant icon, message, and call-to-action.
 */

import { Box, Typography, Button } from '@mui/material';
import { AttachMoney, People, CalendarMonth, AccessTime, Add } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

export type EmptyStateType = 'revenue' | 'clients' | 'bookings' | 'time';

interface EmptyStateProps {
  type: EmptyStateType;
}

const emptyStateConfig: Record<
  EmptyStateType,
  {
    icon: React.ReactNode;
    title: string;
    description: string;
    actionLabel: string;
    actionPath: string;
  }
> = {
  revenue: {
    icon: <AttachMoney sx={{ fontSize: 48, color: 'text.disabled' }} />,
    title: 'No revenue data yet',
    description: 'Create and send your first invoice to start tracking revenue.',
    actionLabel: 'Create Invoice',
    actionPath: '/invoices/new',
  },
  clients: {
    icon: <People sx={{ fontSize: 48, color: 'text.disabled' }} />,
    title: 'No clients yet',
    description: 'Add your first client to start building your business.',
    actionLabel: 'Add Client',
    actionPath: '/clients',
  },
  bookings: {
    icon: <CalendarMonth sx={{ fontSize: 48, color: 'text.disabled' }} />,
    title: 'No bookings yet',
    description: 'Schedule your first booking to see analytics here.',
    actionLabel: 'Create Booking',
    actionPath: '/bookings',
  },
  time: {
    icon: <AccessTime sx={{ fontSize: 48, color: 'text.disabled' }} />,
    title: 'No time entries yet',
    description: 'Start tracking time to see your productivity insights.',
    actionLabel: 'Track Time',
    actionPath: '/time-tracking',
  },
};

export default function EmptyState({ type }: EmptyStateProps) {
  const navigate = useNavigate();
  const config = emptyStateConfig[type];

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        py: 6,
        px: 2,
        textAlign: 'center',
      }}
    >
      {config.icon}
      <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
        {config.title}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: 300 }}>
        {config.description}
      </Typography>
      <Button variant="contained" startIcon={<Add />} onClick={() => navigate(config.actionPath)}>
        {config.actionLabel}
      </Button>
    </Box>
  );
}
